import { useRef, useState } from 'react';
import { useFitnessResource } from '@/hooks/useFitnessResource';
import { useLanguage } from '@/i18n/LanguageProvider';
import { supabase } from '@/integrations/supabase/client';
import { previousExercisePerformance } from '@/lib/fitness/workoutHistory';
import { getFitnessPreferencesCopy } from '@/lib/fitnessPreferencesCopy';

interface Props {
  ownerId: string; exerciseName: string; muscle: string;
  currentSet?: number; timed?: boolean; disabled?: boolean;
  onApply?: (values: { weight: number; reps: number }) => void;
}
async function readRecent(userId: string, assertCurrent: () => void) {
  const { data, error } = await supabase.from('workout_sessions').select('exercises,completed_at')
    .eq('user_id', userId).not('completed_at', 'is', null).lte('completed_at', new Date().toISOString())
    .order('completed_at', { ascending: false }).order('id', { ascending: false }).limit(50);
  assertCurrent(); if (error || !Array.isArray(data)) throw error ?? new Error('History unavailable');
  return data;
}
function PreviousValues({ ownerId, exerciseName, muscle, currentSet = 1, timed, disabled = false, onApply }: Props) {
  const { t, lang } = useLanguage(), copy = getFitnessPreferencesCopy(lang);
  const resource = useFitnessResource(readRecent);
  const latest = useRef({ ownerId, exerciseName, muscle, currentSet, disabled });
  latest.current = { ownerId, exerciseName, muscle, currentSet, disabled };
  if (resource.status === 'loading') return <p role="status" className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (resource.status !== 'ready') return <button type="button" onClick={() => void resource.refresh()} className="min-h-11 text-sm text-primary">{t('common.retry')}</button>;
  if (resource.userId !== ownerId || !resource.data) return null;
  const previous = previousExercisePerformance(resource.data, exerciseName, muscle);
  if (!previous || timed !== undefined && previous.timed !== timed) return null;
  const ownerCurrent = resource.capture();
  const apply = (set: { weight: number; reps: number }) => {
    const now = latest.current;
    if (!onApply || now.disabled || !ownerCurrent() || now.ownerId !== ownerId || now.exerciseName !== exerciseName
      || now.muscle !== muscle || now.currentSet !== currentSet) return;
    onApply({ weight: set.weight, reps: set.reps });
  };
  const matching = previous.sets[currentSet - 1];
  return <div className="space-y-2">
    <p className="text-xs text-muted-foreground">{copy.previousLimit}</p>
    {onApply && matching && <button type="button" disabled={disabled} onClick={() => apply(matching)}
      className="min-h-11 rounded-xl border border-primary/40 bg-primary/10 px-3 text-sm font-medium text-primary disabled:opacity-40">{copy.applyPrevious}</button>}
    {onApply && !matching && <p className="text-xs text-muted-foreground">{copy.noneForSet}</p>}
    <ol className="flex flex-wrap gap-x-4 gap-y-2 text-sm tabular-nums">
      {previous.sets.map((set, index) => <li key={index} className="flex items-center gap-2"><span>
        <span className="text-muted-foreground">{index + 1}. </span>
        {set.weight} {t('fitness.weightUnit')} × {set.reps} {t(previous.timed ? 'fitness.ux.seconds' : 'fitness.workout.reps')}</span>
        {onApply && <button type="button" disabled={disabled} onClick={() => apply(set)}
          className="min-h-11 rounded-lg px-2 text-xs text-primary disabled:opacity-40">{copy.applySet}</button>}
      </li>)}
    </ol>
    {onApply && <p className="text-xs leading-relaxed text-muted-foreground">{copy.previousHint}</p>}
  </div>;
}
export default function WorkoutPreviousSets(props: Props) {
  const { t } = useLanguage(), [open, setOpen] = useState(false);
  return <div className="border-t border-border pt-2">
    <button type="button" aria-expanded={open} onClick={() => setOpen(value => !value)}
      className="min-h-11 text-sm font-medium text-primary">{t('fitness.builder.previousSession')}</button>
    {open && <PreviousValues key={JSON.stringify([props.ownerId, props.exerciseName, props.muscle])} {...props} />}
  </div>;
}
